import Intercom, {
  hide,
  onHide,
  onShow,
  onUnreadCountChange,
  showSpace,
  shutdown,
  update,
} from '@intercom/messenger-js-sdk';
import {
  associatedEntityLookup,
  AssociatedEntityType,
  LegacyShapedAssociatedEntity,
} from '@palmetto/associated-entity';
import React, { useEffect, useState } from 'react';
import { User, UserProjectResponse } from '../../api/user/types';
import { getIntercomJwt } from '../../api/user/user';
import { intercomConfig } from '../../config';
import { useChat } from '../../contexts/ChatContext';

export interface IntercomProps {
  user?: User;
  sunnyChatEnabled?: boolean;
}
const APP_ID = intercomConfig.appId;

const MESSENGER_FRAME_STYLES = `
  @media (min-width: 451px) {
    .intercom-messenger-frame.intercom-messenger-frame.intercom-messenger-frame {
      top: 24px !important;
      bottom: 106px !important;
      height: calc(100vh - 130px) !important;
      max-height: calc(100vh - 130px) !important;
      transition: none !important;
      transform: none !important;
    }
  }
  @media (max-width: 450px) {
    .intercom-messenger-frame.intercom-messenger-frame.intercom-messenger-frame {
      top: env(safe-area-inset-top, 0px) !important;
      bottom: 0px !important;
      height: calc(100vh - env(safe-area-inset-top, 0px)) !important;
      max-height: calc(100vh - env(safe-area-inset-top, 0px)) !important;
    }
  }
`;

function getSabalAccountIds(projects: UserProjectResponse[] | undefined): string {
  return (projects ?? [])
    .flatMap((p) =>
      associatedEntityLookup(p.associatedEntities as LegacyShapedAssociatedEntity[]).getIds(
        AssociatedEntityType.LegacySabalAccount,
      ),
    )
    .join(',');
}

function toUnixSeconds(isoDate: string | undefined): number | undefined {
  const ms = isoDate ? new Date(isoDate).getTime() : NaN;
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

export function bootIntercom() {
  if (!isBrowser()) {
    return;
  }
  Intercom({
    app_id: APP_ID,
    custom_launcher_selector: '.launch-intercom',
    hide_default_launcher: true,
    hide_notifications: true,
  });
}

export function openHome() {
  if (isBrowser()) {
    showSpace('home');
  }
}

export function openHelpCenter() {
  if (isBrowser()) {
    showSpace('help');
  }
}

export function openMessages() {
  if (isBrowser()) {
    showSpace('messages');
  }
}

export function showArticle(articleId: string) {
  if (!isBrowser() || typeof window.Intercom !== 'function') {
    return;
  }
  window.Intercom('showArticle', parseInt(articleId, 10));
}

export function showConversation(conversationId: string) {
  if (typeof window.Intercom === 'function') {
    window.Intercom('showConversation', conversationId);
  }
}

export function showNewMessage(prefilledContent: string) {
  if (typeof window.Intercom === 'function') {
    window.Intercom('showNewMessage', prefilledContent);
  }
}

export function showNewMessageForCase(salesforceCaseId: string, prefilledContent: string) {
  if (isBrowser()) {
    update({ salesforceCaseId });
    showNewMessage(prefilledContent);
  }
}

export function clearSalesforceCaseId() {
  if (isBrowser()) {
    update({ salesforceCaseId: null });
  }
}

export function shutdownIntercom() {
  if (isBrowser()) {
    shutdown();
  }
}

export function hideIntercomMessenger() {
  if (isBrowser()) {
    hide();
  }
}

let resolveIntercomReady: () => void;
export const intercomReady = new Promise<void>((resolve) => {
  resolveIntercomReady = resolve;
});

export function markIntercomReady() {
  resolveIntercomReady();
}

const IntercomBubble: React.FC<IntercomProps> = (props: IntercomProps) => {
  const { user, sunnyChatEnabled = false } = props;
  const { setActiveChat, setUnreadCount, setMessengerOpen } = useChat();
  const [intercomJwt, setIntercomJwt] = useState<string | null>(null);

  useEffect(() => {
    if (user && !intercomJwt) {
      getIntercomJwt()
        .then((response) => {
          setIntercomJwt(response.token);
        })
        .catch((error) => {
          console.error('Failed to fetch Intercom JWT:', error);
        });
    }
  }, [user, intercomJwt]);

  useEffect(() => {
    const shouldSendUserData = user && intercomJwt;

    const fincoIds = shouldSendUserData ? getSabalAccountIds(user.projects) : undefined;

    update({
      app_id: APP_ID,
      ...(shouldSendUserData && {
        user_id: user.ciaCustomerId,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        created_at: toUnixSeconds(user.createdAt),
        intercom_user_jwt: intercomJwt,
        ...(fincoIds && { finco_ids: fincoIds }),
        ...(user.source && { source: user.source }),
        ...(user.segmentation ?? {}),
        userIdSource: 'ciaCustomerId',
      }),
      hide_default_launcher: sunnyChatEnabled,
      hide_notifications: sunnyChatEnabled,
      horizontal_padding: 24,
      vertical_padding: 24,
    });

    if (shouldSendUserData) {
      markIntercomReady();
    }
  }, [user, intercomJwt, sunnyChatEnabled]);

  useEffect(() => {
    const handleShow = () => {
      setActiveChat(true);
      setMessengerOpen(true);
    };
    const handleHide = () => {
      setMessengerOpen(false);
      clearSalesforceCaseId();
    };
    const handleUnreadCountChange = (unreadCount: number) => {
      setUnreadCount(unreadCount);
      if (unreadCount > 0) {
        setActiveChat(true);
      }
    };
    const handleMessageReceived = () => {
      setActiveChat(true);
    };

    // Intercom event listeners
    onShow(handleShow);
    onHide(handleHide);
    // user has unread messages
    onUnreadCountChange(handleUnreadCountChange);
    // new message received
    if (typeof window.Intercom === 'function') {
      window.Intercom('onMessageReceived', handleMessageReceived);
    }
  }, [setActiveChat, setUnreadCount, setMessengerOpen]);

  return <style>{MESSENGER_FRAME_STYLES}</style>;
};

export default IntercomBubble;
